import React, {useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineFullscreen } from 'react-icons/ai';
import { Popover } from 'antd';
import ReactHlsPlayer from './hls-player';
import { Utils } from 'services';
// Live latency, stated rather than inherited.
//
// This was `{}`, so every number below came from hls.js defaults and the only thing setting
// the class's latency was the encoder's segment length. The backend runs ffmpeg with
// `-hls_time 4 -hls_list_size 4`, so EXT-X-TARGETDURATION is 4 and the playlist window is
// ~16s; hls.js then starts playback `liveSyncDurationCount` (3) target durations behind the
// edge = 12s, which is the delay measured end to end (~13s with manifest fetch).
//
// The start delay and the stop delay are not two mechanisms — they are the same number read
// twice. A viewer sits 12s behind live, so a viewer sees the first frame 12s late and keeps
// receiving for 12s after the teacher stops. What made them disagree in practice is that the
// figure only ever grew:
//
//   - hls.js adds 1s to the target for every buffer stall (capped at one target duration, so
//     12s -> 16s here) and resets that only when the manifest is reloaded from scratch.
//   - `liveMaxLatencyDurationCount` defaults to Infinity, which switches OFF the forward seek
//     that would put a drifted viewer back at the live edge. A viewer who buffers resumes
//     where they stalled and stays there for the rest of the class, playing segments the
//     teacher recorded long ago with nothing anywhere telling the teacher it is happening.
//
// Naming the ceiling is what stops the ratchet. 4 target durations = 16s is the point past
// which hls.js seeks back to the sync position, so the delay now lives in a band (12s
// nominal, 16s worst case) instead of climbing without limit, and start and stop stay the
// same number. `liveSyncDurationCount: 3` is written out only to keep the nominal delay
// exactly what it already is — it is the value hls.js was computing anyway.
//
// Going below 12s is a backend change, not this one: with a 4s segment and a 4-segment
// window there is no room to hold less. Lowering `-hls_time` is what buys that.
const hlsConfig = {
  liveSyncDurationCount: 3,
  // This was 4, and it is the only setting we impose that hls.js does not default. That
  // matters, because the same live playlist played in any stock hls.js player sounds fine
  // while ours degrades after twenty minutes — and a difference that only shows up in our
  // player has to come from something only our player does.
  //
  // 4 target durations is 16s, the distance past which hls.js stops waiting and seeks the
  // viewer forward to the live edge. One such seek is a skip. The problem is that the
  // distance ratchets: hls.js adds a second to its latency target on every buffer stall, so
  // a viewer on an ordinary connection creeps backwards all lesson, crosses 16s, gets
  // yanked forward, stalls again, and from then on the class is a sequence of forced seeks.
  // Each one lands mid-segment, which is heard as a click or a burst of mangled audio before
  // the decoder resynchronises — and it gets worse the longer the class runs, which is
  // exactly how it was reported.
  //
  // Infinity is the hls.js default and therefore what every player that sounds fine is
  // using. Restoring it is the honest A/B: if our audio now matches the online player, this
  // was the cause. The trade is real and is why the ceiling was added in the first place —
  // with no ceiling a viewer who buffers never catches up and drifts for the rest of the
  // lesson with nothing telling the teacher. So this is not the resting place: once the
  // audio is confirmed clean, put a high finite number back (8–10, i.e. 32–40s) so drift
  // stays bounded without the player reaching for a seek during a normal class.
  liveMaxLatencyDurationCount: Infinity,
  // 30s was too tight, and it was the last non-default number we imposed on the player.
  //
  // hls.js defaults this to Infinity. At 30s, with 4s segments, the buffer passes the limit
  // within the first half-minute and then every appended segment triggers a
  // SourceBuffer.remove() behind the playhead — an eviction every four seconds, for the whole
  // class, while audio is decoding out of that same buffer. That is a lot of churn to run
  // underneath live playback for the sake of memory we were never short of.
  //
  // It also matches the symptom that is left: brief cracks that a player restart clears and
  // that come back a while later, which is what you would expect from something that only
  // starts once the buffer has grown past a threshold.
  //
  // 90s keeps the original point — a two-hour class must not keep every decoded second — while
  // making eviction rare rather than continuous. A rolling 90s of audio and video is a few tens
  // of megabytes, not a leak.
  backBufferLength: 90,
  maxAudioFramesDrift: 100,        // Tolerate massive WebRTC gaps without throwing MEDIA_ERROR
  stretchShortVideoTrack: true,    // Pad missing video frames to prevent sync crashes
};
  let t2 = null;
// Ascending, and read as a ladder by the stall fallback below as well as by the menu.
const availableQualities = ['360', '480', '720']
const VideoPlayer = (props) => {

  // The caller may want the <video> too — the broadcast page measures a remote teacher's
  // speaking level off the audio this element is already decoding. Sharing the ref rather
  // than adding a second one keeps goToLive and everything else reading the same element.
  const ownRef = useRef(null);
  const videoRef = props.mediaRef ?? ownRef;
  const containerRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const [currentQuality, setCurrentQuality] = useState('480');
  // The rendition the viewer had asked for when the player gave up on it, or null if this
  // quality is the one they chose. Only the HUD reads it — a picture that quietly got
  // smaller with nothing saying why is the same complaint from the other end.
  const [steppedDownFrom, setSteppedDownFrom] = useState(null);
  const [ispopovervisible, setpopovervisible] = useState(false);
  const [isLive, setLive] = useState(false);
  // Escape, F11 and the browser's own control all leave full screen without going through
  // onScreenToggle, so isFullScreen drifted out of step with the document every time. It was
  // only sizing the player before, where being wrong corrected itself on the next toggle;
  // the HUD reads it for its colours now, so a stale value leaves white text on a grey chip.
  useEffect(() => {
    const sync = () => setFullScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  // Two states, because full screen changes what is behind the HUD in a way nothing else
  // does. Windowed, the player is an inset card on a light page and the class fills it —
  // often a whiteboard or a slide deck, i.e. white — so the HUD carries its own light chip
  // and dark ink rather than trying to be legible against whatever is being taught. Full
  // screen, the picture is letterboxed against black and a chip would be a grey patch
  // floating in it, so the controls go plain white with nothing behind them.
  const hudInk = isFullScreen ? '#ffffff' : '#14141c';
  // Only the full-screen case needs it: white over a letterbox is safe, white over the top
  // of a bright frame is not, and the chip already does this job in the windowed case.
  const hudShadow = isFullScreen ? '0 1px 3px rgba(0,0,0,0.55)' : 'none';



  const makeFullScreen = () => {
    if (!containerRef||!containerRef.current) return
    let div = containerRef.current
    if (div.requestFullscreen) 
        div.requestFullscreen();
    else if (div.webkitRequestFullscreen) 
        div.webkitRequestFullscreen();
    else if (div.msRequestFullScreen) 
      div.msRequestFullScreen();
  }

  const onScreenToggle = (e) => {
    e.stopPropagation()
    // No setFullScreen here. The document is the only thing that knows whether the request
    // was granted, and the fullscreenchange listener above reads it back either way — doing
    // it optimistically as well meant a refused or vetoed request left the flag set.
    if (!isFullScreen) {
        makeFullScreen()
    } else {
        document.exitFullscreen();
    }
  }
  const goToLive = () => {
    if (isLive) return
    if (videoRef.current) {
      videoRef.current.currentTime = videoRef.current.duration-2;
      videoRef.current.play();
    }
};
  /*
   * What to do about a viewer whose player keeps stalling: give them a smaller rendition.
   *
   * This is the ABR this player does not otherwise have. The backend publishes each
   * rendition as its own single-level playlist — /360/, /480/, /720/ — so hls.js is never
   * handed a choice and cannot make one. Picking 720p therefore pins a viewer to 720p for
   * the whole class no matter what their line does, and the only recovery the player had
   * was to rebuild itself at the same URL, which restarts the stall it was called for. That
   * is the "it jitters if I keep watching at 720" loop: stall, rebuild, stall.
   *
   * Only downwards, and never back up on its own. A viewer who has just been moved because
   * their line could not hold a rendition is the last viewer who should be returned to it
   * by a timer; the menu is right there if they want to try again, and choosing from it
   * clears the marker and re-arms this.
   *
   * Returning false at the bottom rung hands the decision back: 360p failing is a real
   * fault rather than an ambitious choice, and a rebuilt buffer is the right answer to it.
   */
  const handlePersistentStall = useCallback(() => {
    const index = availableQualities.indexOf(currentQuality);
    if (index <= 0) return false
    const next = availableQualities[index - 1];
    console.log(
      '%c[HLS]%c repeated stalls at ' + currentQuality + 'p — stepping down to ' + next + 'p',
      'color:#fff;background:#0369a1;padding:1px 5px;border-radius:3px;font-weight:700', '');
    setSteppedDownFrom(currentQuality);
    setCurrentQuality(next);
    return true
  }, [currentQuality]);

  const streamUrl = useMemo(()=>{
    const url = `https://broadcast.aulas.in/${Utils.getBroadcastUrls('server')}/videos/${props.streamId}/${currentQuality}/index.m3u8`
    // The request. Whether it is honoured is answered by the [HLS] DECODED SIZE line that
    // follows it — this selector picks a folder, and a folder name is a claim.
    console.log(
      '%c[HLS]%c requesting ' + currentQuality + 'p -> ' + url,
      'color:#fff;background:#0369a1;padding:1px 5px;border-radius:3px;font-weight:700', '');
    return url
  }, [props.streamId, currentQuality]);

  // The wrapper fills its parent: with no size of its own it collapsed to the video's
  // natural height, so a percentage height on the player had nothing to resolve against.
  // objectFit contain shows a shared screen whole rather than cropped to the tile's shape,
  // and sits ahead of the spread so a caller can still ask for something else.
  return (
    <div style={{position:"relative", width:"100%", height:"100%"}} ref={containerRef}>
      <ReactHlsPlayer
        autoPlay={true} 
        style={{objectFit:'contain', ...props.style, width:isFullScreen?'100vw':(props.style && props.style.width) ? props.style.width: '100%', height:isFullScreen?'100vh':(props.style && props.style.height) ? props.style.height: '100%'}} 
        controls={false} 
        playerRef={videoRef}
        hlsConfig={hlsConfig}
        onPersistentStall={handlePersistentStall}
        liveCheckCallback={(bool)=>{
          setLive(bool)
        }}
        src={streamUrl}
        onEndCallback={()=>{
          // Playback ran off the end of the playlist, which for a live stream means the
          // encoder wrote #EXT-X-ENDLIST and everything behind it has now been played out.
          // The broadcast page closes its end-of-class drain on this.
          console.log('%c[HLS]%c playback reached the end of the playlist',
            'color:#fff;background:#0369a1;padding:1px 5px;border-radius:3px;font-weight:700', '');
          props.onEnded?.();
        }}/>
              {/* Always on screen. This used to be revealed by clicking the picture, which
                  meant the one control that says whether you are watching live, the one that
                  changes the quality and the one that goes full screen were all behind a
                  gesture nothing announced — a viewer stuck 30s behind had no way of
                  learning that Live was a button. It sits in its own corner now rather than
                  on a 50%-black strip across the full width of the player, which over a
                  whiteboard read as a grey band cutting the class in half. The chip is the
                  replacement, and it goes away in full screen — see hudInk above. */}
              <div
                className="r-c-fe"
                style={{
                  position:"absolute", bottom:12, right:16, gap:14, pointerEvents:"none",
                  padding:isFullScreen?0:"6px 12px",
                  borderRadius:10,
                  background:isFullScreen?"none":"rgba(243, 242, 247, 0.92)",
                  border:isFullScreen?"none":"1px solid rgba(0, 0, 0, 0.06)",
                  backdropFilter:isFullScreen?"none":"blur(2px)",
                }}>
                <div className='r-c-c' style={{color:hudInk, textShadow:hudShadow, fontWeight:600, fontSize:13, cursor:isLive?"default":"pointer", pointerEvents:"auto"}} onClick={goToLive}>
                  {/* Red only while actually at the live edge. Behind it the dot takes the
                      ink's own colour rather than a fixed grey, which disappeared into a
                      light picture exactly like the text did. */}
                  <div style={{backgroundColor:isLive?"#ef4444":hudInk, opacity:isLive?1:0.45, height:8, width:8, borderRadius:8, marginRight:6}}>

                  </div>
                  <div>
                  Live
                  </div>
                </div>
                <Popover getPopupContainer={() => containerRef.current} open={ispopovervisible} color={"#3e3e3e"} content={
                          <div style={{padding:5, paddingLeft:10, paddingRight:10, display:"flex", flexDirection:"column", alignItems:"center", backgroundColor:"none"}}>
                              {
                                availableQualities.map((item)=>(
                                  <div style={{fontWeight:"500", color:item===currentQuality?"black":"white", backgroundColor:item===currentQuality?"white":null, padding:5, borderRadius:5, cursor:"pointer"}} onClick={()=>{setSteppedDownFrom(null); setCurrentQuality(item); setpopovervisible(false)}}>{item}p</div>
                                ))
                              }
                          </div>
                } title={null} trigger="click" onOpenChange={setpopovervisible} style={{zIndex:10}}>
                  <div style={{cursor:"pointer", pointerEvents:"auto"}} onClick={(e)=>{
                    e.stopPropagation();
                    setpopovervisible(!ispopovervisible)}}>
                    {/* The marker is not decoration. A viewer who chose 720p and is now
                        watching 480p will otherwise read the smaller picture as the class
                        getting worse, which is the same misdiagnosis the teacher's end of
                        this was built to avoid — and without it there is nothing to say the
                        menu is worth revisiting. */}
                    <div style={{color:hudInk, textShadow:hudShadow, fontWeight:700, fontSize:13}}
                         title={steppedDownFrom
                           ? `${steppedDownFrom}p kept buffering on this connection — switched to ${currentQuality}p. Tap to choose again.`
                           : 'Video quality'}>
                      {currentQuality}p{steppedDownFrom ? <span style={{fontWeight:500, opacity:0.6}}> · auto</span> : null}
                    </div>
                  </div>
                </Popover>
                <div style={{color:hudInk, pointerEvents:"auto"}} className='r-c-c cursor-pointer' onClick={onScreenToggle}><AiOutlineFullscreen style={{color:hudInk, fontSize:20, filter:isFullScreen?"drop-shadow(0 1px 3px rgba(0,0,0,0.55))":"none"}}/></div>
              </div>
                </div>
  );
};

export default VideoPlayer;